import axios from 'axios'
import {
  rebuildHtml,
  isHtmlString,
  extractHtmlInnerText
} from './helper.js'

const GEMINI_API_BASE_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-3.5-flash:generateContent'

// Request timeout (ms) - 30 minutes
const REQUEST_TIMEOUT = 30 * 60 * 1000

/**
 * Get Gemini API key from localStorage.
 * @returns {String} The API key or empty string.
 */
export function getGeminiApiKey() {
  return localStorage.getItem('GEMINI_API_KEY') || ''
}

/**
 * Save Gemini API key to localStorage.
 * @param {String} key - The API key to save.
 */
export function setGeminiApiKey(key) {
  localStorage.setItem('GEMINI_API_KEY', key || '')
}

/**
 * Translates a single item from Vietnamese to target locales using Gemini API.
 * Translates all translatable keys in a SINGLE API call.
 * For HTML string values, extracts inner text via regex before sending to API, then maps back into HTML template.
 *
 * @param {Object} item - The item to translate.
 * @param {Array} locales - Target locale codes (e.g., ['en', 'ko', 'zh']).
 * @param {Array} columns - The columns definitions.
 * @param {String} type - The entity type (e.g., 'artifact', 'place').
 * @returns {Promise<Object>} - The translated item.
 */
export async function translateItemGemini(item, locales, columns, type) {
  const API_KEY = getGeminiApiKey()
  if (!API_KEY) {
    throw new Error('Gemini API Key chưa được cấu hình. Vui lòng nhập API Key ở bước đầu tiên.')
  }

  const GEMINI_API_URL = `${GEMINI_API_BASE_URL}?key=${API_KEY}`

  const targetLocales = locales.filter(l => l !== 'vi')
  if (targetLocales.length === 0) {
    return item
  }

  const languageColumns = columns.filter((c) => c.language && !(c.languageExcludeTypes || []).includes(type))
  const translatableKeys = languageColumns.map(c => c.key)

  // Deep-clone the item and initialize locale structures
  const translatedItem = JSON.parse(JSON.stringify(item))
  targetLocales.forEach(loc => {
    if (!translatedItem[loc]) {
      translatedItem[loc] = {}
    }
    translatableKeys.forEach(key => {
      if (translatedItem[loc][key] === undefined) {
        translatedItem[loc][key] = ''
      }
    })
  })

  // Prepare payload for all translatableKeys at once
  const payloadToTranslate = {}
  const htmlMetadata = {}

  translatableKeys.forEach(key => {
    const value = item[key]
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return
    }

    if (isHtmlString(value)) {
      const { isHtml, template, texts } = extractHtmlInnerText(value)
      if (isHtml && texts.length > 0) {
        htmlMetadata[key] = { template, texts }
        payloadToTranslate[key] = texts.map(t => t.text)
      } else {
        payloadToTranslate[key] = value
      }
    } else {
      payloadToTranslate[key] = value
    }
  })

  if (Object.keys(payloadToTranslate).length === 0) {
    return item
  }

  const localeList = targetLocales.join(',')

  const prompt = `Translate the following JSON object from Vietnamese to target languages: [${localeList}].
Return a JSON object where top-level keys are target language codes (${localeList}), and each value is an object containing the translated key-value pairs matching the EXACT same JSON key structure as the input object.

Input JSON:
${JSON.stringify(payloadToTranslate, null, 2)}`

  try {
    const response = await axios.post(
      GEMINI_API_URL,
      {
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          responseMimeType: 'application/json',
          temperature: 0
        }
      },
      { timeout: REQUEST_TIMEOUT }
    )

    const responseText = response.data.candidates[0].content.parts[0].text
    const translationResult = JSON.parse(responseText)

    targetLocales.forEach(loc => {
      const locData = translationResult[loc] || {}

      translatableKeys.forEach(key => {
        if (locData[key] !== undefined) {
          if (htmlMetadata[key]) {
            const translatedChunks = Array.isArray(locData[key]) ? locData[key] : [locData[key]]
            const translatedTextsMap = {}
            const originalTexts = htmlMetadata[key].texts

            originalTexts.forEach((tObj, idx) => {
              const translatedCoreText = translatedChunks[idx] !== undefined ? translatedChunks[idx] : tObj.text
              translatedTextsMap[tObj.index] = tObj.leading + translatedCoreText + tObj.trailing
            })

            translatedItem[loc][key] = rebuildHtml(htmlMetadata[key].template, translatedTextsMap)
          } else {
            translatedItem[loc][key] = locData[key]
          }
        }
      })
    })
  } catch (error) {
    console.error(`Failed to translate item:`, error.message || error)
    translatedItem._hasError = true
    translatedItem._errorMessage = error.message || 'Lỗi gọi API Gemini'
  }

  // Map back to the original item structure with translations merged
  const result = { ...item }
  if (translatedItem._hasError) {
    result._hasError = true
    result._errorMessage = translatedItem._errorMessage
  }
  targetLocales.forEach(loc => {
    result[loc] = { ...(item[loc] || {}), ...translatedItem[loc] }
  })
  return result
}
